import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { configureSprig, createSprigActions } from './sprigManager'
import type {
  SprigConfig,
  SprigIdentifyOptions,
  SprigTrackOptions,
} from './types'

interface SprigContextValue {
  identify: (options: SprigIdentifyOptions) => void
  track: (options: SprigTrackOptions) => void
  logout: () => void
  isConfigured: boolean
}

const SprigContext = createContext<SprigContextValue | null>(null)

interface SprigProviderProps {
  config: SprigConfig
  children: React.ReactNode
  autoIdentify?: {
    userId?: string
    anonymousId?: string
    attributes?: Record<string, string | number | boolean>
  }
}

export const SprigProvider = ({
  config,
  children,
  autoIdentify,
}: SprigProviderProps) => {
  const sprigActions = useMemo(() => createSprigActions(config), [config])
  const [isConfigured, setIsConfigured] = useState(false)

  useEffect(() => {
    if (!config.environmentId) {
      return
    }

    configureSprig(config)
      .then(() => {
        setIsConfigured(true)

        // Auto-identify if provided
        if (autoIdentify) {
          sprigActions.identify(autoIdentify)
        }
      })
      .catch(() => {
        // Configuration failed, but we don't set isConfigured to true
        // This allows the component to render but actions won't work
      })
  }, [sprigActions, autoIdentify, config])

  const contextValue: SprigContextValue = useMemo(
    () => ({
      identify: sprigActions.identify,
      track: sprigActions.track,
      logout: sprigActions.logout,
      isConfigured,
    }),
    [sprigActions, isConfigured],
  )

  return (
    <SprigContext.Provider value={contextValue}>
      {children}
    </SprigContext.Provider>
  )
}

export const useSprig = (): SprigContextValue => {
  const context = useContext(SprigContext)
  if (!context) {
    throw new Error('useSprig must be used within a SprigProvider')
  }
  return context
}
